import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../contexts/CartContext';

export const Cart = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { cart, updateQuantity, removeFromCart, clearCart, getCartTotal } = useCart();

  const cartTotal = getCartTotal();

  const handleUpdateQuantity = (productId, newQuantity) => {
    updateQuantity(productId, newQuantity);
    if (newQuantity > 0) {
      toast({
        title: "Cantidad actualizada",
        description: "Se actualizó la cantidad del producto"
      });
    }
  };

  const handleRemoveFromCart = (productId) => {
    removeFromCart(productId);
    toast({
      title: "Producto eliminado",
      description: "Se eliminó el producto del carrito"
    });
  };

  const handleClearCart = () => {
    clearCart();
    toast({
      title: "Carrito vaciado",
      description: "Se eliminaron todos los productos"
    });
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <ShoppingBag className="h-24 w-24 text-slate-600 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-white mb-4">Tu carrito está vacío</h1>
          <p className="text-slate-400 text-lg mb-8">Agrega productos para comenzar tu compra</p>
          <Button 
            onClick={() => navigate('/shop')} 
            className="bg-gradient-to-r from-cyan-600 to-emerald-600 text-lg px-8 py-6"
          >
            Ir a la Tienda
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container mx-auto max-w-6xl">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-5xl font-bold bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
            Tu Carrito
          </h1>
          <Button 
            variant="outline" 
            onClick={handleClearCart}
            className="border-red-500 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Vaciar Carrito
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => (
              <Card key={item.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
                <CardContent className="p-6">
                  <div className="flex gap-6">
                    <div className="w-24 h-24 rounded-lg overflow-hidden bg-slate-900 flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex justify-between mb-2">
                        <div>
                          <h3 className="text-xl font-bold text-white">{item.name}</h3>
                          <p className="text-slate-400 text-sm">{item.description}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFromCart(item.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>

                      <div className="flex justify-between items-center mt-4">
                        <div className="flex items-center gap-3">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                            className="border-slate-700 h-10 w-10 p-0"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="text-white font-semibold text-lg w-12 text-center">
                            {item.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                            className="border-slate-700 h-10 w-10 p-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="text-right">
                          {item.discount > 0 ? (
                            <div className="flex flex-col items-end">
                              <p className="text-slate-400 text-sm line-through">
                                Precio original: ${item.price.toLocaleString()}
                              </p>
                              <p className="text-emerald-400 text-sm font-bold">
                                Con {item.discount}% OFF: ${Math.round(item.price * (1 - item.discount / 100)).toLocaleString()}
                              </p>
                              <p className="text-2xl font-bold text-cyan-400">
                                Total: ${(Math.round(item.price * (1 - item.discount / 100)) * item.quantity).toLocaleString()}
                              </p>
                            </div>
                          ) : (
                            <>
                              <p className="text-slate-400 text-sm">Precio unitario: ${item.price.toLocaleString()}</p>
                              <p className="text-2xl font-bold text-cyan-400">
                                ${(item.price * item.quantity).toLocaleString()}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="lg:col-span-1">
            <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 sticky top-24">
              <CardHeader>
                <CardTitle className="text-white">Resumen del Pedido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex justify-between text-slate-300">
                    <span>Productos ({cart.length})</span>
                    <span>${cartTotal.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-slate-700 pt-3">
                    <div className="flex justify-between text-xl font-bold">
                      <span className="text-white">Total</span>
                      <span className="text-cyan-400">${cartTotal.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Button 
                  onClick={() => navigate('/checkout')}
                  className="w-full bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500 text-lg py-6"
                >
                  Continuar con la Compra
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>

                <Button 
                  onClick={() => navigate('/shop')}
                  variant="outline"
                  className="w-full border-slate-700 text-slate-300"
                >
                  Seguir Comprando
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
